import React, { useMemo } from 'react';
import { useQuery } from 'react-query';

import { getWithdrawalsApi } from '@api/billing-publisher.api';
import ConfigurableTable from '@uikit/old/ConfigurableTable/ConfigurableTable';
import Pagination from '@uikit/old/Pagination/Pagination';
import usePagination from '@hooks/usePagination';
import useCurrentWorkspaceId from '@hooks/useCurrentWorkspaceId';
import useWorkspaceMembers from '@hooks/useWorkspaceMembers';

import { BillingPublisherWithdrawalType } from '@ts/billing-publisher.types';

import { mapWithdrawalStatus } from '../../constants';
import { useWithdrawalPdfDownload } from '../../hooks';
import { formatUsd, mapPaymentMethod } from '../../utils';

import { withdrawalsTableConfig, WithdrawalsTableRenderProps } from './tableConfig';

function WithdrawalsTable(): JSX.Element {
  const workspaceId = useCurrentWorkspaceId();
  const { members } = useWorkspaceMembers();
  const { page, changePage, limit, changeLimit } = usePagination();
  const { activeId, download } = useWithdrawalPdfDownload(workspaceId);

  const { data: withdrawalsData = [] } = useQuery<BillingPublisherWithdrawalType[]>(
    ['withdrawals-list', page, limit],
    () => getWithdrawalsApi(page - 1, limit, ''),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      refetchInterval: 30 * 60 * 1000,
    }
  );

  const rows = useMemo(
    () =>
      withdrawalsData.map((w) => {
        const fee = Number(w.fee ?? 0);
        return {
          ...w,
          withdrawalBy: members.find((member) => member.userId === w.withdrawalByUserId),
          statusFormatted: mapWithdrawalStatus(w.status),
          amountFormatted: formatUsd(w.amount),
          // `fee` is the Wise fee in our USD balance currency; only surfaced when > 0.
          feeFormatted: fee > 0 ? formatUsd(fee) : '',
          paymentMethodFormatted: mapPaymentMethod(w.paymentSettings),
        };
      }),
    [withdrawalsData, members]
  );

  const renderProps: WithdrawalsTableRenderProps = {
    onDownload: download,
    activeDownloadId: activeId,
  };

  return (
    <>
      <ConfigurableTable
        data={rows}
        config={withdrawalsTableConfig}
        renderProps={renderProps}
        testId="publisher-withdrawals-table"
        rowTestId={(row) => `publisher-withdrawals-row-${row.id}`}
      />
      <Pagination
        curPage={page}
        onChangePage={changePage}
        pageLimit={limit}
        onChangePageLimit={changeLimit}
        totalItems={withdrawalsData[0]?.totalItems}
      />
    </>
  );
}

export default WithdrawalsTable;
